/**
 * ProductsTab — shows accounts tied to this CIF via CustAcctRelInq.
 *
 * v0.1.45+ (Team 3 weekend sprint): each row now exposes per-account maint
 * actions that open the corresponding dialog:
 *   - Manage Beneficiaries (DDA/SAV/CD) → PodBeneficiaryDialog
 *   - Add Joint Owner                    → JointOwnerAddDialog
 *   - NSF/OD Override (DDA)              → NsfOdOverrideDialog
 *   - Re-issue Card (DDA/SAV)            → CardReissueDialog
 *
 * Dialogs are account-scoped; they take acctId + acctType. They fire session
 * events + auto-tasks via their dedicated hooks.
 */
import { useEffect, useState } from 'react';
import { DataGrid, Pill, RowActionsMenu } from '@/components/primitives';
import type { RowAction } from '@/components/primitives';
import { unwrapJxrlArray, txt } from '@/utils/jxrl';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuth } from '@/contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { PodBeneficiaryDialog } from '../maint-actions/pod-beneficiary/PodBeneficiaryDialog';
import { JointOwnerAddDialog } from '../maint-actions/joint-owner-add/JointOwnerAddDialog';
import { NsfOdOverrideDialog } from '../maint-actions/nsf-od-override/NsfOdOverrideDialog';
import { CardReissueDialog } from '../maint-actions/card-reissue/CardReissueDialog';
import type { MaintenanceActionInfo } from '@retail-ops/shared-types';
import { useMaintenanceSwitches, TEST_GATE_MESSAGE } from '@/components/maintenance/useMaintenanceSwitches';
import { MaintenanceStartDialog, type MaintenanceTarget } from '@/components/maintenance/MaintenanceStartDialog';

export interface ProductsTabProps {
  cifId: string;
  /** Test gate on and this customer isn't a test customer: every change action is disabled with the reason. */
  testGateBlocks?: boolean;
}

interface AccountRow {
  acctId: string;
  acctType: string;
  relCode: string;
  acctRelCode?: string;
  // Enrichment (Bill 2026-09-11): balance / F17 opened / F20 status / F103 card status.
  balance?: number | null;
  openedOn?: string | null;
  acctStat?: string | null;
  acctStatDesc?: string | null;
  cardStat?: string | null;
  cardStatDesc?: string | null;
  cardLast4?: string | null;
  // Card linked to this DEPOSIT account (Bill 2026-10-16) - last 4 only.
  linkedCardStat?: string | null;
  linkedCardStatDesc?: string | null;
  linkedCardLast4?: string | null;
  note?: string | null;
}

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Core returns dates as ISO or CCYYMMDD; show MM/DD/YYYY either way. */
function fmtOpened(raw?: string | null): string {
  const v = (raw ?? '').trim();
  if (!v) return '—';
  const m = /^(\d{4})-?(\d{2})-?(\d{2})/.exec(v);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  return v;
}

type DialogKind = 'pod' | 'joint' | 'nsf' | 'card' | null;

interface ActiveDialog {
  kind: DialogKind;
  row?: AccountRow;
}

function typeLabel(code: string): string {
  switch (code) {
    case '10': return 'DDA';
    case '20': return 'SAV';
    case '30': return 'CD';
    case '40': return 'LOAN';
    case '50': return 'SDB';
    case '60': return 'IRA';
    case '91': return 'ATM';
    default:   return code;
  }
}

// Role-based visibility gates for per-row buttons. Matches the authZ gates
// on the server routes (these are UX affordances; the Function enforces).
const SUPERVISOR_ROLES = new Set<string>([
  'Supervisor', 'Manager', 'Executive', 'Admin', 'SuperAdmin',
]);
const FSR_OR_ABOVE = new Set<string>([
  'Officer', 'Supervisor', 'Manager', 'Executive', 'Admin', 'SuperAdmin',
]);

export function ProductsTab({ cifId, testGateBlocks = false }: ProductsTabProps): JSX.Element {
  const { client, makeSignal } = useApi();
  const { user } = useAuth();
  const [rows, setRows] = useState<AccountRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<ActiveDialog>({ kind: null });
  const [reloadToken, setReloadToken] = useState<number>(0);
  // Account maintenance (Bill 2026-10-17): actions an administrator switched on, built on the opening ceremony.
  const maint = useMaintenanceSwitches();
  const [maintStart, setMaintStart] = useState<{ action: MaintenanceActionInfo; target: MaintenanceTarget } | null>(null);

  useEffect(() => {
    const signal = makeSignal();
    // ENRICHED FIRST (Bill 2026-09-11: "Balance and Opened columns are blank -
    // Card Status is not appearing"). The server fans out AcctInq per account
    // and EFTCardInq per card; if that route is unavailable we fall back to the
    // raw relationship list so the tab never goes dark.
    client.accounts.custAcctRelInqEnriched(cifId, { signal }).then(
      (res): void => {
        const list = res?.accounts ?? [];
        setRows(
          list
            .map((r): AccountRow => ({
              acctId: r.acctId,
              acctType: r.acctType,
              relCode: r.relCode ?? r.acctRelCode ?? '',
              ...(r.acctRelCode ? { acctRelCode: r.acctRelCode } : {}),
              balance: r.balance ?? null,
              openedOn: r.openedOn ?? null,
              acctStat: r.acctStat ?? null,
              acctStatDesc: r.acctStatDesc ?? null,
              cardStat: r.cardStat ?? null,
              cardStatDesc: r.cardStatDesc ?? null,
              cardLast4: r.cardLast4 ?? null,
              linkedCardStat: r.linkedCardStat ?? null,
              linkedCardStatDesc: r.linkedCardStatDesc ?? null,
              linkedCardLast4: r.linkedCardLast4 ?? null,
              note: r.note ?? null,
            }))
            .filter((a) => Boolean(a.acctId) && Boolean(a.acctType)),
        );
      },
      (err: unknown): void => {
        if (err instanceof Error && err.name === 'AbortError') return;
        // Fallback: the raw relationship list (id/type/rel only).
        client.accounts.custAcctRelInq(cifId, { signal }).then(
          (res): void => {
            const env = res as unknown as { success?: boolean; error?: string };
            if (env.success === false) {
              setError(env.error ?? 'Unable to load account relationships');
              return;
            }
            const raw = res as unknown as Record<string, unknown>;
            const dataNode =
              (raw['data'] as Record<string, unknown> | undefined) ??
              (raw['Data'] as Record<string, unknown> | undefined) ??
              raw;
            const recs = unwrapJxrlArray<Record<string, unknown>>(
              dataNode,
              'CustAcctRelInqRecArray',
              'CustAcctRelInqRec',
            );
            setRows(
              recs
                .map((r): AccountRow => {
                  const acctObj = r['AcctId'];
                  let acctId = '';
                  let acctType = '';
                  if (typeof acctObj === 'string') {
                    acctId = acctObj;
                  } else if (acctObj && typeof acctObj === 'object') {
                    const inner = acctObj as Record<string, unknown>;
                    acctId = txt(inner['AcctId']);
                    acctType = txt(inner['AcctType']);
                  }
                  if (!acctType) acctType = txt(r['AcctType']);
                  const acctRelCode = txt(r['AcctRelCode']);
                  return {
                    acctId,
                    acctType,
                    relCode: txt(r['RelCode']) || acctRelCode,
                    ...(acctRelCode ? { acctRelCode } : {}),
                    note: 'Balance, date opened and status unavailable (enriched lookup failed).',
                  };
                })
                .filter((a) => Boolean(a.acctId) && Boolean(a.acctType)),
            );
          },
          (err2: unknown): void => {
            if (err2 instanceof Error && err2.name === 'AbortError') return;
            setError(err2 instanceof Error ? err2.message : 'Unknown error');
          },
        );
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cifId, reloadToken]);

  if (error) return <EmptyState title="Unable to load accounts" message={error} />;
  if (rows === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--sp-9)' }}>
        <LoadingSpinner label="Loading related accounts…" />
      </div>
    );
  }

  // Suppress Role / Acct rel entirely when jXchange returned them blank for
  // EVERY row — a column of em-dashes reads as broken data, not as absent
  // data, and costs the account number its width (Bill 2026-07-15).
  const anyRelCode = rows.some((r) => Boolean(r.relCode));
  const anyAcctRelCode = rows.some((r) => Boolean(r.acctRelCode));

  const canOperate = Boolean(user?.primaryRole && FSR_OR_ABOVE.has(user.primaryRole));
  const canOverrideNsf = Boolean(user?.primaryRole && FSR_OR_ABOVE.has(user.primaryRole));
  // Read-only flags surface to dialogs.
  const readOnlyNsf = !canOverrideNsf;
  // Lesser roles can open the NSF dialog but must supply supervisor approval.
  void SUPERVISOR_ROLES; // tracked for future finer-grained gates.

  // Actions collapse into a per-row overflow menu (Bill 2026-07-15). Four
  // outlined buttons on every row meant 44 identical pills on an 11-account
  // customer — every row shouting equally, so nothing read as important, and the
  // account number (the thing an officer actually scans for) fought for space
  // with controls they use once a visit.
  //
  // Eligibility still decides what EXISTS; `disabled` decides what's actionable.
  // An action the officer lacks permission for stays visible-but-disabled with
  // the reason on hover — seeing that a thing exists and is unavailable beats
  // wondering whether the app forgot it.
  const renderActions = (r: AccountRow): JSX.Element => {
    const isDeposit = r.acctType === '10' || r.acctType === '20' || r.acctType === '30';
    const isDda = r.acctType === '10';
    // Re-issue Card gated to CHECKING only (Bill 2026-07-13 r2 / BACKLOG-0871:
    // it was showing on savings). Savings can carry an ATM card, but that's the
    // exception — surface it only when a linked EFTCard is actually detected
    // (follow-up: EFTCardSrch per SAV row before offering re-issue).
    const isCardEligible = isDda;
    const noPerm = testGateBlocks ? TEST_GATE_MESSAGE : 'You do not have permission to run maintenance on this account.';
    const canChange = canOperate && !testGateBlocks;

    const actions: RowAction[] = [];
    // Account maintenance first: each switched-on action that applies to this kind of account.
    for (const a of maint.actions.filter((x) => x.accountTypes.includes(r.acctType))) {
      actions.push({
        key: `maint-${a.code}`, label: a.title,
        onClick: () => setMaintStart({ action: a, target: { acctId: r.acctId, acctType: r.acctType, label: `${r.acctType === '10' ? 'Checking' : r.acctType === '20' ? 'Savings' : 'Account'} ··${r.acctId.slice(-4)}` } }),
        disabled: !canChange, disabledReason: noPerm,
      });
    }
    // The older dialogs write to Core with no form or signature, or don't work (2026-10-17 review); they show only
    // while an administrator keeps maint.legacyRcmActions on.
    if (!maint.legacyRcmActions) {
      return (
        <RowActionsMenu actions={actions} ariaLabel={`Actions for account ${r.acctId}`}
          emptyFallback={<span style={{ color: 'var(--pcb-text-disabled, #9aa8b6)' }}>—</span>} />
      );
    }
    if (isDeposit) {
      actions.push({
        key: 'pod', label: 'Manage beneficiaries',
        onClick: () => setActive({ kind: 'pod', row: r }),
        disabled: !canChange, disabledReason: noPerm,
      });
      actions.push({
        key: 'joint', label: 'Add joint owner',
        onClick: () => setActive({ kind: 'joint', row: r }),
        disabled: !canChange, disabledReason: noPerm,
      });
    }
    if (isDda) {
      actions.push({
        key: 'nsf', label: 'NSF / OD override',
        onClick: () => setActive({ kind: 'nsf', row: r }),
        disabled: !canChange, disabledReason: noPerm,
      });
    }
    if (isCardEligible) {
      actions.push({
        key: 'card', label: 'Re-issue card',
        onClick: () => setActive({ kind: 'card', row: r }),
        disabled: !canChange, disabledReason: noPerm,
      });
    }

    return (
      <RowActionsMenu
        actions={actions}
        ariaLabel={`Actions for account ${r.acctId}`}
        // Loans/NetTeller rows have no maintenance actions here yet. Render a
        // muted dash rather than an empty cell so the column reads as
        // "nothing to do", not "failed to load".
        emptyFallback={<span style={{ color: 'var(--pcb-text-disabled, #9aa8b6)' }}>—</span>}
      />
    );
  };

  const handleSaved = (): void => {
    // Force a reload of the accounts list so any rel/status changes show up.
    setReloadToken((t) => t + 1);
  };

  const handleClose = (): void => {
    setActive({ kind: null });
  };

  return (
    <>
      <DataGrid<AccountRow>
        rowKey={(r) => `${r.acctId}-${r.acctType}`}
        columns={[
          // WIDTHS MATTER HERE (Bill 2026-07-15). Suppressing Role/Acct rel freed
          // their space, and acctId had NO width — so in a width:100% table it
          // absorbed all the slack and sat left-aligned in a giant cell while Type
          // and the ⋮ were shoved to the far right. A canyon down the middle:
          // worse than the em-dashes it replaced. Constrain the real columns and
          // let an explicit spacer eat the remainder, so the data stays grouped
          // left and the actions stay pinned right.
          { key: 'acctId',   label: 'Account #', render: (r) => <code style={{ fontFamily: 'var(--font-mono)' }}>{r.acctId}</code>, width: '150px' },
          { key: 'acctType', label: 'Type',      render: (r) => <Pill variant="info">{typeLabel(r.acctType)}</Pill>, width: '80px' },
          // Enriched columns (Bill 2026-09-11). Each cell shows the value or an
          // honest dash; the row's `note` explains a blank on hover.
          {
            key: 'balance', label: 'Balance', width: '120px',
            render: (r) => (
              <span title={r.note ?? undefined} style={{ fontFamily: 'var(--font-mono)' }}>
                {r.acctType === '91' ? '—' : (typeof r.balance === 'number' ? money.format(r.balance) : '—')}
              </span>
            ),
          },
          {
            key: 'opened', label: 'Opened', width: '110px',
            render: (r) => <span title={r.note ?? undefined}>{fmtOpened(r.openedOn)}</span>,
          },
          {
            key: 'status', label: 'Status', width: '130px',
            render: (r) => {
              // F103 for a card account, F20 for everything else - one column,
              // because a row is one or the other.
              if (r.acctType === '91') {
                if (!r.cardStat && !r.cardStatDesc) return <span title={r.note ?? undefined}>—</span>;
                const tone = r.cardStat === 'A' ? 'ok' : (r.cardStat === 'I' || r.cardStat === 'O') ? 'info' : 'warn';
                return (
                  <span title={`Card status F103 = ${r.cardStat ?? '?'}${r.cardLast4 ? ` · card …${r.cardLast4}` : ''}`}>
                    <Pill variant={tone}>{r.cardStatDesc ?? r.cardStat}</Pill>
                  </span>
                );
              }
              const code = (r.acctStat ?? '').trim();
              const tone = code === '' ? 'ok' : code === 'C' ? 'warn' : 'info';
              // The card linked to this deposit account (EFTCardAcctId), Bill 2026-10-16.
              const hasCard = Boolean(r.linkedCardStat || r.linkedCardStatDesc);
              const cardTone = r.linkedCardStat === 'A' ? 'ok' : (r.linkedCardStat === 'I' || r.linkedCardStat === 'O') ? 'info' : 'warn';
              return (
                <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                  <span title={`Account status F20 = ${code === '' ? '(blank = Active)' : code}`}>
                    <Pill variant={tone}>{r.acctStatDesc ?? (code || 'Active')}</Pill>
                  </span>
                  {hasCard && (
                    <span title={`Linked card status F103 = ${r.linkedCardStat ?? '?'}${r.linkedCardLast4 ? ` · card …${r.linkedCardLast4}` : ''}`}>
                      <Pill variant={cardTone}>{`Card${r.linkedCardLast4 ? ` …${r.linkedCardLast4}` : ''}: ${r.linkedCardStatDesc ?? r.linkedCardStat}`}</Pill>
                    </span>
                  )}
                </span>
              );
            },
          },
          // Role / Acct rel are SUPPRESSED WHEN EMPTY rather than rendering a
          // column of em-dashes (Bill 2026-07-15). jXchange leaves RelCode and
          // AcctRelCode blank for a large share of CIFs, so on most customers
          // these were two columns of "—" — noise that reads as broken data and
          // costs the account number its width. When the data IS there they're
          // genuinely useful, so show them then and only then.
          ...(anyRelCode
            ? [{ key: 'relCode', label: 'Role', render: (r: AccountRow) => r.relCode, width: '80px' }]
            : []),
          ...(anyAcctRelCode
            ? [{ key: 'acctRelCode', label: 'Acct rel', render: (r: AccountRow) => r.acctRelCode ?? '—', width: '100px' }]
            : []),
          // Spacer: absorbs the leftover width so nothing else has to. Without a
          // column that WANTS the slack, the browser hands it to whichever column
          // lacks a width - which is how the canyon appeared.
          { key: '_spacer', label: '', render: () => null },
          { key: 'actions', label: '', render: renderActions, width: '56px' },
        ]}
        rows={rows}
        emptyMessage="No accounts linked to this CIF."
      />

      {active.kind === 'pod' && active.row && (
        <PodBeneficiaryDialog
          open
          onClose={handleClose}
          onSaved={handleSaved}
          cifId={cifId}
          acctId={active.row.acctId}
          acctType={active.row.acctType}
          officerCode={user?.officerCode ?? '000'}
          readOnly={!canOperate}
        />
      )}
      {active.kind === 'joint' && active.row && (
        <JointOwnerAddDialog
          open
          onClose={handleClose}
          onSaved={handleSaved}
          cifId={cifId}
          acctId={active.row.acctId}
          acctType={active.row.acctType}
          officerCode={user?.officerCode ?? '000'}
          readOnly={!canOperate}
        />
      )}
      {active.kind === 'nsf' && active.row && (
        <NsfOdOverrideDialog
          open
          onClose={handleClose}
          onSaved={handleSaved}
          cifId={cifId}
          acctId={active.row.acctId}
          acctType={active.row.acctType}
          officerCode={user?.officerCode ?? '000'}
          readOnly={readOnlyNsf}
        />
      )}
      {active.kind === 'card' && active.row && (
        <CardReissueDialog
          open
          onClose={handleClose}
          onSaved={handleSaved}
          cifId={cifId}
          acctId={active.row.acctId}
          acctType={active.row.acctType}
          officerCode={user?.officerCode ?? '000'}
          readOnly={!canOperate}
        />
      )}
      <MaintenanceStartDialog open={maintStart !== null} action={maintStart?.action ?? null} target={maintStart?.target ?? null}
        cif={cifId} testCustomersOnly={maint.testCustomersOnly} onClose={() => setMaintStart(null)} />
    </>
  );
}
